import { FieldConfig, formatFieldValue, getNestedValue } from "./config";

/** One labeled value on the record's secondary line. The rendering layer
 *  decides how to style the label vs. the value (typically muted label,
 *  normal-weight value). */
export interface SecondaryItem {
  label: string;
  value: string;
}

/**
 * Result of summarizing a loaded record. The component decides how to render
 * primary vs secondary (typically two lines: bold primary, then a secondary
 * line of `label value · label value · …` items).
 */
export interface RecordSummary {
  /** Formatted value of the first field in the schema config. Falls back to
   *  the raw entity ID when the primary field is empty. */
  primary: string;
  /** Labeled values for the remaining fields, with empties suppressed. The
   *  rendering layer formats each `{label, value}` and joins them with " · ". */
  secondary: SecondaryItem[];
}

/** Treat these raw values as "no data" and drop the field from the summary.
 *  Critically retains `0` and `false` — both are meaningful values
 *  (e.g. `paid_amount: 0`, `is_active: false`). */
const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === "";

/**
 * Build a two-part summary for a loaded record.
 *
 * Used by `LoadedRecordsList` to replace the legacy datagrid columns. The old
 * grid rendered every configured field as a column and showed "—" for empties;
 * the screenshot review surfaced this as the core "Loaded Records looks bad"
 * complaint. This helper drops empty fields entirely so the summary only shows
 * data that actually exists.
 *
 * @param entity         Raw entity object (typically from `useGetMany`).
 * @param fields         Schema's field config (`ENTITY_CONFIG[schema].fields`).
 *                       The first entry is treated as the primary identifier.
 * @param claimantLookup Optional id→name map for fields with format `claimant_id_ref`.
 *
 * Falls back to `"—"` for the primary value only when both the primary field
 * and the entity ID are unusable. Secondary array can be empty.
 */
export const buildRecordSummary = (
  entity: Record<string, unknown> | null,
  fields: FieldConfig[],
  claimantLookup?: Record<string, string>
): RecordSummary => {
  if (!entity || fields.length === 0) {
    return { primary: "—", secondary: [] };
  }

  const [primaryField, ...secondaryFields] = fields;
  const primaryRaw = getNestedValue(entity, primaryField.field);

  let primary: string;
  if (!isEmpty(primaryRaw)) {
    primary = formatFieldValue(primaryRaw, primaryField.format, claimantLookup);
    // `claimant_id_ref` format may resolve to "—" if the claimant isn't in the
    // lookup yet (still loading). Treat that as "no primary" so we surface an
    // ID fallback rather than a stray dash.
    if (primary === "—") {
      const id = entity.id;
      primary = typeof id === "string" && id.length > 0 ? id : "—";
    }
  } else {
    const id = entity.id;
    primary = typeof id === "string" && id.length > 0 ? id : "—";
  }

  const secondary: SecondaryItem[] = [];
  for (const field of secondaryFields) {
    const raw = getNestedValue(entity, field.field);
    if (isEmpty(raw)) continue;
    const formatted = formatFieldValue(raw, field.format, claimantLookup);
    // `formatFieldValue` returns "—" only when the raw value was empty, which
    // we already filtered above — but `claimant_id_ref` can return "—" when
    // the lookup misses. Don't pollute the summary with a dash in that case.
    if (formatted === "—") continue;
    secondary.push({ label: field.label, value: formatted });
  }

  return { primary, secondary };
};
